import { action, observable, runInAction } from 'mobx';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { evacuationController } from './evacuationController';
import {
  EvacuationPriceRequest,
  TowTruckDriver,
  TowTruckDriverRequest,
  BaseTruckPoint,
  CreateRefusalRequest,
  MapCornersInterface,
  EvacuatorOrderReqestInterface,
  EvacuatorOrderListItemInterface,
  AvarcomOrderListItemInterface,
  AvarcomFiltersInterface,
  TBankAvarcomOrderListItemInterface,
  TBankAvarcomOrderFileItemInterface,
  OrdersByValuesRequestInterface,
  OrdersByValuesItemInterface,
  OrdersByValuesBadgeType,
  TEvacuatorOrderItemInterface,
  RouteRequestInterface,
} from '../../api/evacuationApi';
import { PriceResponse } from '../../../scheduler/api/priceCalculateApi';
import type { RouteFeature } from '@yandex/ymaps3-types';

export interface EvacuationStore {
  evacuationPriceResponse: PriceResponse | null;
  towTruckDrivers: TowTruckDriver[];
  towTruckDriverRequest: TowTruckDriverRequest | null;
  baseTruckPoints: BaseTruckPoint[];
  baseTruckPointsByOrderId: string | null;
  mapCorners: MapCornersInterface | null;
  mouseOverOfficeId: string | null;
  error: any;
  clustererPins: boolean;
  evacuatorOrders: EvacuatorOrderListItemInterface[];
  avarcomOrders: AvarcomOrderListItemInterface[];
  showOnlyTomorrowOrders: boolean;
  avarcomFilters: AvarcomFiltersInterface;
  tBankAvarcomOrders: TBankAvarcomOrderListItemInterface[];
  tBankAvarcomOrderFiles: TBankAvarcomOrderFileItemInterface[];
  tBankAvarcomOrderFilesByOrderId: string | null;
  ordersByValues: OrdersByValuesItemInterface[];
  ordersByValuesRequest: OrdersByValuesRequestInterface | null;
  badgeValues: OrdersByValuesBadgeType;
  selectedBadgeValue: string | null;
  ordersByValuesType: string | null;
  ordersByValuesToFindStrings: string;
  tEvacuatorOrders: EvacuatorOrderListItemInterface[];
  tEvacuatorOrdersReport: TEvacuatorOrderItemInterface[];
  route: RouteFeature | null;
  newOrderIds: string[]; // ID заявок, пришедших через сокет (ещё не кликнутые)
  evacuatorOrdersRequest: EvacuatorOrderReqestInterface | null; // последний применённый запрос списка эвакуатор-заявок
}

const DEFAULT_AVARCOM_FILTERS: AvarcomFiltersInterface = {
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export const evacuationStore: EvacuationStore = observable({
  evacuationPriceResponse: null,
  towTruckDrivers: [],
  towTruckDriverRequest: null, // исправлено с {} на null согласно типу
  baseTruckPoints: [],
  baseTruckPointsByOrderId: null,
  mapCorners: null,
  mouseOverOfficeId: null,
  error: null,
  clustererPins: true,
  evacuatorOrders: [],
  avarcomOrders: [],
  showOnlyTomorrowOrders: false,
  avarcomFilters: DEFAULT_AVARCOM_FILTERS,
  tBankAvarcomOrders: [],
  tBankAvarcomOrderFiles: [],
  tBankAvarcomOrderFilesByOrderId: null,
  ordersByValues: [],
  ordersByValuesRequest: null,
  badgeValues: [],
  selectedBadgeValue: null,
  ordersByValuesType: null,
  ordersByValuesToFindStrings: '',
  tEvacuatorOrders: [],
  tEvacuatorOrdersReport: [],
  route: null,
  newOrderIds: [],
  evacuatorOrdersRequest: null,
} as EvacuationStore);

// Дебаунс-обновление списка эвакуатор-заявок (повторяет последний применённый запрос)
const _debouncedRefreshEvacuatorOrders = debounce(async () => {
  try {
    await evacuationActions.getEvacuatorOrders(evacuationStore.evacuatorOrdersRequest ?? {});
  } catch {
    // игнорируем ошибку повторного обновления
  }
}, 500, { leading: true, trailing: false });

export const evacuationActions = {
  // Получение цены эвакуации
  getEvacuationPriceResponse: async (request: EvacuationPriceRequest): Promise<void> => {
    runInAction(() => { evacuationStore.error = null; });
    const response = await evacuationController.getEvacuationPriceResponse(request);
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.evacuationPriceResponse = response.data ?? null;
    });
  },

  clearEvacuationPriceResponse: action(() => {
    evacuationStore.evacuationPriceResponse = null;
  }),

  // Получение водителей эвакуаторов
  getTowTruckDrivers: async (request: TowTruckDriverRequest): Promise<void> => {
    runInAction(() => {
      evacuationStore.error = null;
      evacuationStore.towTruckDriverRequest = request;
    });
    const response = await evacuationController.getTowTruckDrivers(request);
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.towTruckDrivers = response.data?.map((v) => ({ ...v, id: v.userId })) ?? [];
    });
  },

  clearTowTruckDrivers: action(() => {
    evacuationStore.towTruckDrivers = [];
    evacuationStore.towTruckDriverRequest = null;
  }),

  // Централизованная обработка ошибок (уже action)
  handleError: action((response) => {
    if (response.errorData) {
      const { data } = response;
      evacuationStore.error = data;
      throw data;
    }
  }),

  // Точки базирования эвакуаторов
  getBaseTruckPoints: async (orderId: string): Promise<void> => {
    runInAction(() => { evacuationStore.error = null; });
    const response = await evacuationController.getBaseTruckPoints(orderId);
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.baseTruckPoints = response.data?.items ?? [];
      evacuationStore.baseTruckPointsByOrderId = orderId;
    });
  },

  // Установка отказа на точку (синхронный, но оставлен async для совместимости)
  setRefusalToPoint: async (request: CreateRefusalRequest): Promise<void> => {
    runInAction(() => {
      evacuationStore.error = null;
      evacuationStore.baseTruckPoints = evacuationStore.baseTruckPoints.map((v) => {
        if (v.partnerOfficeId === request.partnerOfficeId) {
          return {
            ...v,
            ...request,
            refusalComment: request?.comment,
            refusalDate: moment().unix(),
          };
        }
        return v;
      });
    });
  },

  createRefusal: async (request: CreateRefusalRequest): Promise<void> => {
    const response = await evacuationController.createRefusal(request);
    evacuationActions.handleError(response);
  },

  setClustererPins: async (newValue: boolean): Promise<void> => {
    runInAction(() => {
      evacuationStore.clustererPins = newValue;
    });
  },

  setMapCorners: async (request: MapCornersInterface): Promise<void> => {
    if (isEqual(request, evacuationStore.mapCorners)) return;
    runInAction(() => {
      evacuationStore.mapCorners = request;
    });
  },

  // Заявки эвакуаторов
  getEvacuatorOrders: async (request: EvacuatorOrderReqestInterface): Promise<void> => {
    runInAction(() => {
      evacuationStore.error = null;
      evacuationStore.evacuatorOrdersRequest = request;
    });
    try {
      const response = await evacuationController.getEvacuatorOrders(request);
      evacuationActions.handleError(response);
      runInAction(() => {
        evacuationStore.evacuatorOrders =
          response.data.items?.sort((a, b) => b.createdDate - a.createdDate) ?? [];
      });
    } catch {
      // Ошибка уже установлена в handleError, дополнительно можно игнорировать
    }
  },

  clearEvacuatorOrders: action(() => {
    evacuationStore.evacuatorOrders = [];
    evacuationStore.evacuatorOrdersRequest = null;
  }),

  refreshEvacuatorOrdersList: action(() => {
    _debouncedRefreshEvacuatorOrders();
  }),

  cancelPendingEvacuatorRefresh: action(() => {
    _debouncedRefreshEvacuatorOrders.cancel();
  }),

  // Заявки аваркомов
  getAvarcomOrders: async (): Promise<void> => {
    runInAction(() => { evacuationStore.error = null; });
    const response = await evacuationController.getAvarcomOrders();
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.avarcomOrders =
        response.data.items?.sort((a, b) => b.createdDate - a.createdDate) ?? [];
    });
  },

  setShowOnlyTomorrowOrders: async (value: boolean): Promise<void> => {
    if (evacuationStore.showOnlyTomorrowOrders === value) return;
    runInAction(() => {
      evacuationStore.showOnlyTomorrowOrders = value;
    });
  },

  clearAvarcomOrders: action(() => {
    evacuationStore.avarcomOrders = [];
  }),

  setAvarcomFilters: async (value: AvarcomFiltersInterface): Promise<void> => {
    if (isEqual(evacuationStore.avarcomFilters, value)) return;
    runInAction(() => {
      evacuationStore.avarcomFilters = value;
    });
  },

  clearAvarcomFilters: action(() => {
    evacuationStore.avarcomFilters = { ...DEFAULT_AVARCOM_FILTERS };
  }),

  setMouseOverOfficeId: async (mouseOverOfficeId: string): Promise<void> => {
    if (isEqual(mouseOverOfficeId, evacuationStore.mouseOverOfficeId)) return;
    runInAction(() => {
      evacuationStore.mouseOverOfficeId = mouseOverOfficeId;
    });
  },

  clearMouseOverOfficeId: action(() => {
    if (evacuationStore.mouseOverOfficeId === null) return;
    runInAction(() => {
      evacuationStore.mouseOverOfficeId = null;
    });
  }),

  // Заявки Т-Банка
  getTBankAvarcomOrders: async (): Promise<void> => {
    runInAction(() => { evacuationStore.error = null; });
    const response = await evacuationController.getTBankAvarcomOrders();
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.tBankAvarcomOrders =
        response.data.items?.sort((a, b) => b.createdDate - a.createdDate) ?? [];
    });
  },

  clearTBankAvarcomOrders: action(() => {
    evacuationStore.tBankAvarcomOrders = [];
  }),

  getBankAvarcomOrderFiles: async (orderId: string): Promise<void> => {
    runInAction(() => {
      evacuationStore.error = null;
      evacuationStore.tBankAvarcomOrderFilesByOrderId = orderId;
    });
    const response = await evacuationController.getOrderPhotos(orderId, false);
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.tBankAvarcomOrderFiles = response.data ?? [];
    });
  },

  clearBankAvarcomOrderFiles: action(() => {
    evacuationStore.tBankAvarcomOrderFiles = [];
    evacuationStore.tBankAvarcomOrderFilesByOrderId = null;
  }),

  // Поиск заявок по значениям
  getOrderByValues: async (
    request: OrdersByValuesRequestInterface
  ): Promise<OrdersByValuesItemInterface[]> => {
    runInAction(() => {
      evacuationStore.error = null;
      evacuationStore.ordersByValuesRequest = request;
    });
    const response = await evacuationController.getOrderByValues(request);
    evacuationActions.handleError(response);
    const items = response.data?.items ?? [];
    runInAction(() => {
      evacuationStore.ordersByValues = items;
    });
    return items;
  },

  cleartOrderByValues: action(() => {
    evacuationStore.ordersByValues = [];
    evacuationStore.ordersByValuesRequest = null;
  }),

  setBadgeValues: action((request: OrdersByValuesBadgeType) => {
    evacuationStore.badgeValues = request;
  }),

  clearBadgeValues: action(() => {
    evacuationStore.badgeValues = [];
  }),

  setSelectedBadgeValue: action((request: string) => {
    evacuationStore.selectedBadgeValue = request;
  }),

  clearSelectedBadgeValue: action(() => {
    evacuationStore.selectedBadgeValue = null;
  }),

  setOrdersByValuesType: action((request: string) => {
    evacuationStore.ordersByValuesType = request;
  }),

  setOrdersByValuesToFindStrings: action((request: string) => {
    evacuationStore.ordersByValuesToFindStrings = request;
  }),

  // Заявки Т-Эвакуатора
  getTEvacuatorOrders: async (): Promise<void> => {
    runInAction(() => { evacuationStore.error = null; });
    const response = await evacuationController.getTEvacuatorOrders();
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.tEvacuatorOrders =
        response.data.items?.sort((a, b) => b.createdDate - a.createdDate) ?? [];
    });
  },

  clearTEvacuatorOrders: action(() => {
    evacuationStore.tEvacuatorOrders = [];
  }),

  getTEvacuatorOrdersReport: async (): Promise<void> => {
    runInAction(() => { evacuationStore.error = null; });
    const response = await evacuationController.getTEvacuatorOrdersReport();
    evacuationActions.handleError(response);
    runInAction(() => {
      evacuationStore.tEvacuatorOrdersReport = response.data.items ?? [];
    });
  },

  clearTEvacuatorOrdersReport: action(() => {
    evacuationStore.tEvacuatorOrdersReport = [];
  }),

  getRoute: async (request: RouteRequestInterface): Promise<any> => {
    // Заглушка, реализация не требуется на данный момент
    if (!request) return [];
    // TODO: реализовать при необходимости
    return [];
  },

  clearRoute: action(() => {
    evacuationStore.route = null;
  }),

  addNewOrderId: action((orderId: string) => {
    if (!evacuationStore.newOrderIds.includes(orderId)) {
      evacuationStore.newOrderIds.push(orderId);
    }
  }),

  removeNewOrderId: action((orderId: string) => {
    evacuationStore.newOrderIds = evacuationStore.newOrderIds.filter((id) => id !== orderId);
  }),

  clearNewOrderIds: action(() => {
    evacuationStore.newOrderIds = [];
  }),
};